import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../layout/Icon.jsx';
import { useNotificationCentre } from './useNotificationCentre.js';

const DOT = {
  danger: '#EF4444',
  warning: '#F59E0B',
  info: '#0EA5E9'
};

function timeAgo(value) {
  const t = value ? new Date(value).getTime() : 0;
  if (!t) return '';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return 'الآن';
  const min = Math.floor(sec / 60);
  if (min < 60) return `قبل ${min} دقيقة`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `قبل ${hr} ساعة`;
  return `قبل ${Math.floor(hr / 24)} يوم`;
}

/** وجهة النقر: المخزون → شاشة المخزون، الجلسة → محاسبة الطفل. */
function targetOf(n) {
  if (n.category === 'inventory') return '/inventory';
  if (n.category === 'sessions') return n.sessionId ? `/checkout/${n.sessionId}` : '/active';
  return null;
}

/**
 * جرس الإشعارات — مكوّن جاهز للتركيب في الشريط العلوي.
 * مكتفٍ بذاته: الزر + القائمة + الإغلاق بالنقر خارجه.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { notifications, unread, markAllRead, markRead, loading } = useNotificationCentre();

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = () => {
    setOpen((v) => {
      if (!v && unread > 0) markAllRead();
      return !v;
    });
  };

  const onPick = (n) => {
    const to = targetOf(n);
    markRead(n._id);
    setOpen(false);
    if (to) navigate(to);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative w-[38px] h-[38px] rounded-[10px] bg-white border border-line grid place-items-center shadow-card"
        title="إشعارات"
        type="button"
        onClick={toggle}
      >
        <Icon name="bell" size={18} />
        {unread > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red text-white text-[10px] font-bold grid place-items-center ring-2 ring-paper">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute top-[44px] left-0 w-[340px] card p-0 z-20 max-h-[420px] overflow-y-auto">
          <div className="card-h flex items-center justify-between">
            <div className="font-bold text-[13px]">الإشعارات</div>
            <div className="text-[11px] text-ink-3">{notifications.length} تنبيه قائم</div>
          </div>

          {loading && notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-ink-3 text-xs">جارٍ التحميل…</div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-ink-3 text-xs">لا توجد إشعارات</div>
          ) : (
            <div className="flex flex-col">
              {notifications.map((n) => (
                <button
                  key={n._id}
                  type="button"
                  onClick={() => onPick(n)}
                  className="w-full text-right px-4 py-2.5 border-t border-line text-[12.5px] hover:bg-paper-2"
                  style={{ background: n.read ? undefined : '#FFFBEB' }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                      style={{ background: DOT[n.severity] || DOT.info }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{n.title || 'إشعار'}</div>
                      {n.message ? (
                        <div className="text-ink-2 text-[11.5px] mt-0.5 leading-relaxed">{n.message}</div>
                      ) : null}
                      <div className="text-ink-3 text-[10.5px] mt-1">{timeAgo(n.raisedAt)}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default NotificationBell;
